"use client";

import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "@/store";

export type HeroContextType = {
  isLoadingAnimationFinished: boolean;
  /**
   * This function is called when asset loading is finished and the mandatory
   * loading animation time has passed.
   * @returns {void}
   */
  onFinishLoadingAnimation: () => void;
};

export const HeroContext = createContext<HeroContextType>({
  isLoadingAnimationFinished: false,
  onFinishLoadingAnimation: () => {},
});

/**
 * The HeroProvider component used to share state
 * between the animated background and other components.
 * @param param0 {React.ReactNode} - The children
 * @returns {React.ReactNode} - The children wrapped in the HeroContext.Provider
 */
export function HeroProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>): React.ReactNode {
  const isHomeVisited = useStore((state) => state.isHomeVisited);
  const setIsHomeVisited = useStore((state) => state.setIsHomeVisited);

  /**
   * If the user has already visited the page, we assume the loading animation
   * is finished.
   */
  const [isLoadingAnimationFinished, setIsLoadingAnimationFinished] = useState(isHomeVisited);

  const onFinishLoadingAnimation = useCallback(() => {
    setIsLoadingAnimationFinished(true);
  }, []);

  useEffect(() => {
    /** We have this check so that the children components get a chance
     * to be initialized with false if isHomeVisited is false to begin with
     */
    if (!isLoadingAnimationFinished) {
      setIsHomeVisited(true);
    }
  }, [setIsHomeVisited, isLoadingAnimationFinished]);

  return (
    <HeroContext.Provider
      value={useMemo(
        () => ({
          isLoadingAnimationFinished,
          onFinishLoadingAnimation,
        }),
        [isLoadingAnimationFinished, onFinishLoadingAnimation],
      )}
    >
      {children}
    </HeroContext.Provider>
  );
}
